import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import InputLabel from '@mui/material/InputLabel'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Box from '@mui/material/Box'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Clear from '@mui/icons-material/Clear'
import Delete from '@mui/icons-material/Delete'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  dpciWithHyphenRegex,
  dpciWithoutHyphenRegex,
  tcinRegex,
} from '../../helpers/Regexes'
import { fetchProductDetailsApi } from '../../betasearch/store/apiService'

const CONCURRENCY = 6

const ProductInfoCard = ({
  updateParentState = () => {},
  selectedAssetGoldMetadata = {},
  isBulkEdit = false,
}) => {
  const [radioBtnProduct, setRadioBtnProduct] = useState('APPEND')
  const [productCode, setProductCode] = useState('')
  const [productInformation, setProductInformation] = useState({
    tcins: [],
    dpcis: [],
  })
  const [chips, setChips] = useState([])
  const [accordionData, setAccordionData] = useState([])

  const validatingSetRef = useRef(new Set())

  const splitEntries = (text) =>
    Array.from(
      new Set(
        text
          .split(/[,\s]+/)
          .map((e) => e.trim())
          .filter(Boolean),
      ),
    )

  const stageChips = (entries) => {
    const existing = new Set(chips.map((c) => c.label))
    const toAdd = entries.filter((code) => !existing.has(code))
    if (toAdd.length) {
      setChips((prev) => [
        ...prev,
        ...toAdd.map((code) => ({
          label: code,
          isError: false,
          isValid: false,
          isLoading: true,
        })),
      ])
    }
    return toAdd
  }

  const validateCode = async (code) => {
    const isTCIN = tcinRegex.test(code)
    const isDPCI =
      dpciWithHyphenRegex.test(code) || dpciWithoutHyphenRegex.test(code)

    if (!isTCIN && !isDPCI) {
      return { code, type: null, isValid: false, data: null }
    }

    try {
      if (isTCIN) {
        const res = await fetchProductDetailsApi('tcin', code)
        return {
          code,
          type: 'tcin',
          isValid: Boolean(res?.data),
          data: res?.data ?? null,
        }
      } else {
        const res = await fetchProductDetailsApi('dpci', code)
        return {
          code,
          type: 'dpci',
          isValid: Boolean(res?.data),
          data: res?.data ?? null,
        }
      }
    } catch {
      return { code, type: null, isValid: false, data: null }
    }
  }

  const applyValidationResult = ({ code, type, isValid, data }) => {
    setChips((prev) =>
      prev.map((chip) =>
        chip.label === code
          ? { ...chip, isLoading: false, isError: !isValid, isValid }
          : chip,
      ),
    )

    validatingSetRef.current.delete(code)

    if (!isValid || !data) return

    setProductInformation((prev) => {
      const tcins = new Set(prev.tcins)
      const dpcis = new Set(prev.dpcis)
      const tcinVal = type === 'tcin' ? code : data.tcin || ''
      const dpciVal = type === 'dpci' ? code : data.dpci || ''
      if (tcinVal) tcins.add(tcinVal)
      if (dpciVal) dpcis.add(dpciVal)
      return { tcins: Array.from(tcins), dpcis: Array.from(dpcis) }
    })

    setAccordionData((prev) => {
      const product = {
        tcin: data.tcin || (type === 'tcin' ? code : ''),
        dpci: data.dpci || (type === 'dpci' ? code : ''),
        brandName: data.product_brand?.brand || '',
        productDescription: data.product_description?.title || '',
      }
      const key = `${product.tcin}_${product.dpci}`
      if (prev.some((p) => `${p.tcin}_${p.dpci}` === key)) return prev
      return [...prev, product]
    })
  }

  const mapWithConcurrency = async (limit, items, mapper) => {
    let i = 0
    const workers = Array.from(
      { length: Math.min(limit, items.length) },
      async () => {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const idx = i++
          if (idx >= items.length) break
          await mapper(items[idx], idx)
        }
      },
    )
    await Promise.all(workers)
  }

  const runValidation = async (codes) => {
    const queue = codes.filter((c) => !validatingSetRef.current.has(c))
    queue.forEach((c) => validatingSetRef.current.add(c))
    if (!queue.length) return
    await mapWithConcurrency(CONCURRENCY, queue, async (code) => {
      const result = await validateCode(code)
      applyValidationResult(result)
    })
  }

  // Initialize from gold metadata (single-asset pre-fill)
  useEffect(() => {
    const itemInfoArray = selectedAssetGoldMetadata?.metadata?.item_info || []
    const tcins = []
    const dpcis = []
    itemInfoArray.forEach((itemInfo) => {
      if (itemInfo.tcin) {
        tcins.push(itemInfo.tcin)
      } else if (itemInfo.dpci) {
        dpcis.push(itemInfo.dpci)
      }
    })

    if (tcins.length > 0 || dpcis.length > 0) {
      setProductInformation({ tcins, dpcis })
      setChips([
        ...tcins.map((tcin) => ({
          label: tcin,
          isError: false,
          isValid: true,
          isLoading: false,
        })),
        ...dpcis.map((dpci) => ({
          label: dpci,
          isError: false,
          isValid: true,
          isLoading: false,
        })),
      ])
      setAccordionData(
        itemInfoArray.map((itemInfo) => ({
          tcin: itemInfo?.tcin || 'N/A',
          dpci: itemInfo?.dpci || 'N/A',
          brandName: itemInfo?.brand || 'N/A',
          productDescription: itemInfo?.product_description?.title || 'N/A',
        })),
      )
    }
  }, [selectedAssetGoldMetadata])

  // Propagate product information changes to parent
  useEffect(() => {
    updateParentState({
      tcins: { data: productInformation.tcins },
      dpcis: { data: productInformation.dpcis },
      unsavedChanges: true,
    })
  }, [productInformation])

  const handlePaste = (event) => {
    event.preventDefault()
    const entries = splitEntries(event.clipboardData.getData('text'))
    const toValidate = stageChips(entries)
    setProductCode('')
    runValidation(toValidate)
  }

  const handleChipInputKeyDown = (event) => {
    if (event.key === 'Backspace' && productCode === '' && chips.length > 0) {
      const lastChip = chips[chips.length - 1].label
      handleDeleteChip(lastChip)()
      return
    }

    if (
      (event.key === 'Enter' || event.key === ',') &&
      productCode.trim() !== ''
    ) {
      event.preventDefault()
      const entries = splitEntries(productCode)
      const toValidate = stageChips(entries)
      setProductCode('')
      runValidation(toValidate)
    }
  }

  const handleDeleteChip = (chipToDelete) => () => {
    setChips((prev) => prev.filter((chip) => chip.label !== chipToDelete))
    validatingSetRef.current.delete(chipToDelete)

    setProductInformation((prev) => ({
      ...prev,
      tcins: prev.tcins.filter((tcin) => tcin !== chipToDelete),
      dpcis: prev.dpcis.filter((dpci) => dpci !== chipToDelete),
    }))

    setAccordionData((prev) =>
      prev.filter(
        (product) =>
          product.tcin !== chipToDelete && product.dpci !== chipToDelete,
      ),
    )
  }

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="product-codes-content"
        id="product-codes-header"
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
          Product Codes
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isBulkEdit && (
          <RadioGroup
            row
            value={radioBtnProduct}
            onChange={(e) => {
              setRadioBtnProduct(e.target.value)
              updateParentState({ radioBtnProduct: e.target.value })
            }}
            sx={{ mb: 1, justifyContent: 'flex-start' }}
          >
            <FormControlLabel
              value="APPEND"
              control={<Radio size="small" />}
              label="Adding to existing data"
            />
            <FormControlLabel
              value="MERGE"
              control={<Radio size="small" />}
              label="Replacing existing data"
            />
          </RadioGroup>
        )}
        <TextField
          id="productCode"
          onChange={(event) => setProductCode(event.target.value)}
          value={productCode}
          placeholder="Add TCIN or DPCIs"
          label="Product code(s)"
          fullWidth
          onKeyDown={handleChipInputKeyDown}
          onPaste={handlePaste}
          helperText="DPCI format: ###-##-#### (with leading zeroes included). Press Enter after adding each code."
          variant="outlined"
          sx={{
            '& .MuiFormHelperText-root': {
              color: '#d32f2f',
            },
            '& .MuiInputBase-root': {
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              rowGap: 0.5,
              maxHeight: 150,
              overflowY: 'auto',
              p: 1,
            },
            '& .MuiInputAdornment-root': {
              m: 0,
              maxWidth: '100%',
              flexBasis: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            },
            '& .MuiInputBase-input': {
              flex: '1 0 120px',
              minWidth: 120,
              p: '6px 0',
            },
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {chips.map((chip, index) => (
                  <Tooltip
                    key={index}
                    title={
                      chip.isLoading
                        ? 'Validating...'
                        : chip.isError
                          ? 'Kindly check the TCIN/DPCI you are sharing. The input is either incorrect or unavailable.'
                          : 'Valid Input'
                    }
                  >
                    <Chip
                      label={
                        chip.isLoading ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: 'inherit' }}
                          />
                        ) : (
                          chip.label
                        )
                      }
                      onDelete={handleDeleteChip(chip.label)}
                      sx={{
                        backgroundColor: chip.isLoading
                          ? '#FFECB3'
                          : chip.isError
                            ? '#FFCDD2'
                            : '#C8E6C9',
                        color: chip.isLoading
                          ? '#FF6F00'
                          : chip.isError
                            ? '#B71C1C'
                            : '#2E7D32',
                      }}
                      deleteIcon={
                        <Clear
                          style={{
                            color: chip.isLoading
                              ? '#FF6F00'
                              : chip.isError
                                ? '#B71C1C'
                                : '#2E7D32',
                            width: 16,
                            height: 16,
                          }}
                        />
                      }
                    />
                  </Tooltip>
                ))}
              </Box>
            ),
          }}
        />
        {accordionData.map((product, index) => (
          <Accordion key={index} sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{ m: 0 }}
            >
              <Grid container spacing={1} display="flex" alignItems="center">
                <Grid item xs={3}>
                  <InputLabel shrink>TCIN</InputLabel>
                  <span>{product.tcin || 'N/A'}</span>
                </Grid>
                <Grid item xs={4}>
                  <InputLabel shrink>DPCI</InputLabel>
                  <span>{product.dpci || 'N/A'}</span>
                </Grid>
                <Grid item xs={4}>
                  <InputLabel shrink>Brand Name</InputLabel>
                  <span>{product.brandName || 'N/A'}</span>
                </Grid>
                <Grid item xs={1}>
                  <Delete onClick={handleDeleteChip(product.tcin)} />
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails sx={{ m: 0 }}>
              <Table sx={{ width: '100%' }}>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: '500',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        color: 'rgba(0, 0, 0, 1)',
                      }}
                    >
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        color: 'rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      {product.productDescription || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: '500',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        color: 'rgba(0, 0, 0, 1)',
                      }}
                    >
                      Brand Name
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        color: 'rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      {product.brandName || 'N/A'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

ProductInfoCard.propTypes = {
  updateParentState: PropTypes.func,
  selectedAssetGoldMetadata: PropTypes.object,
  isBulkEdit: PropTypes.bool,
}

export default ProductInfoCard
